import { useEffect, useState } from 'react';
import api, { formatINR } from '@/lib/api';
import {
  Package,
  Users,
  ListBullets,
  Tag,
  CurrencyInr,
  Clock,
  CheckCircle,
  Truck,
  XCircle,
  Calendar,
  Archive,
  TrendUp,
  Warning,
  Info,
  ChartBar,
} from '@phosphor-icons/react';
import { ADMIN } from '@/constants/testIds';

const STATUS_PALETTE = {
  placed: 'bg-cream-200 text-stone-800',
  confirmed: 'bg-sky-100 text-sky-800',
  packed: 'bg-amber-100 text-amber-800',
  out_for_delivery: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-olive-100 text-olive-800',
  cancelled: 'bg-red-100 text-red-700',
};

const STATUS_LABEL = {
  placed: 'Placed',
  confirmed: 'Confirmed',
  packed: 'Packed',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    api.get('/admin/stats').then((r) => setStats(r.data)).catch(() => {});
    api.get('/orders/admin/all', { params: { limit: 5 } }).then((r) => setOrders(r.data)).catch(() => {});
    api.get('/admin/insights/dashboard').then((r) => setInsights(r.data)).catch(() => {});
  }, []);

  const cards = [
    { testId: 'admin-dash-card-total', label: 'Total Orders', value: stats?.total_orders ?? '—', icon: ListBullets, color: 'olive' },
    { testId: ADMIN.dashCardToday, label: 'Orders Today', value: stats?.today_orders ?? '—', icon: Calendar, color: 'terracotta' },
    { testId: 'admin-dash-card-pending', label: 'Pending', value: stats?.pending_orders ?? '—', icon: Clock, color: 'terracotta' },
    { testId: ADMIN.dashCardPacked, label: 'Packed', value: stats?.packed_orders ?? '—', icon: Archive, color: 'olive' },
    { testId: ADMIN.dashCardOfd, label: 'Out for Delivery', value: stats?.out_for_delivery_orders ?? '—', icon: Truck, color: 'terracotta' },
    { testId: 'admin-dash-card-delivered', label: 'Delivered', value: stats?.delivered_orders ?? '—', icon: CheckCircle, color: 'olive' },
    { testId: ADMIN.dashCardCancelled, label: 'Cancelled', value: stats?.cancelled_orders ?? '—', icon: XCircle, color: 'olive' },
    { testId: ADMIN.dashCardTodayRevenue, label: "Today's Revenue", value: stats ? formatINR(stats.today_revenue || 0) : '—', icon: CurrencyInr, color: 'terracotta' },
    { testId: 'admin-dash-card-gross-revenue', label: 'Revenue (Gross)', value: stats ? formatINR(stats.gross_revenue) : '—', icon: CurrencyInr, color: 'terracotta' },
    { testId: 'admin-dash-card-products', label: 'Products', value: stats?.products ?? '—', icon: Package, color: 'olive' },
    { testId: 'admin-dash-card-categories', label: 'Categories', value: stats?.categories ?? '—', icon: Tag, color: 'olive' },
    { testId: 'admin-dash-card-customers', label: 'Customers', value: stats?.customers ?? '—', icon: Users, color: 'olive' },
  ];

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl text-stone-900">Dashboard</h1>

      {/* Smart Business Insights (rule-based) */}
      {insights && (
        <div data-testid="smart-insights" className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div data-testid="insight-today-sales" className="bg-olive-50 border border-olive-100 rounded-2xl p-4">
              <div className="flex items-center justify-between"><p className="text-xs text-olive-800 uppercase tracking-wider">Today&apos;s Sales</p><CurrencyInr size={20} weight="duotone" className="text-olive-700"/></div>
              <p className="font-heading text-2xl text-stone-900 mt-2">{formatINR(insights.today.sales)}</p>
            </div>
            <div data-testid="insight-today-orders" className="bg-terracotta-50 border border-terracotta-100 rounded-2xl p-4">
              <div className="flex items-center justify-between"><p className="text-xs text-terracotta-800 uppercase tracking-wider">Today&apos;s Orders</p><ListBullets size={20} weight="duotone" className="text-terracotta-700"/></div>
              <p className="font-heading text-2xl text-stone-900 mt-2">{insights.today.orders}</p>
            </div>
            <div data-testid="insight-today-profit" className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
              <div className="flex items-center justify-between"><p className="text-xs text-emerald-800 uppercase tracking-wider">Today&apos;s Profit</p><TrendUp size={20} weight="duotone" className="text-emerald-700"/></div>
              <p className="font-heading text-2xl text-stone-900 mt-2">{formatINR(insights.today.profit)}</p>
            </div>
            <div data-testid="insight-aov" className="bg-sky-50 border border-sky-100 rounded-2xl p-4">
              <div className="flex items-center justify-between"><p className="text-xs text-sky-800 uppercase tracking-wider">Avg Order Value</p><ChartBar size={20} weight="duotone" className="text-sky-700"/></div>
              <p className="font-heading text-2xl text-stone-900 mt-2">{formatINR(insights.today.avg_order_value)}</p>
            </div>
          </div>

          {insights.insights?.length > 0 && (
            <div data-testid="insights-list" className="bg-white border border-stone-200 rounded-2xl p-5">
              <h2 className="font-heading text-lg text-stone-900 mb-3">Business Insights</h2>
              <ul className="space-y-2">
                {insights.insights.map((ins, i) => {
                  const Icon = ins.type === 'positive' ? TrendUp : ins.type === 'warning' ? Warning : Info;
                  const color = ins.type === 'positive' ? 'text-olive-700 bg-olive-50 border-olive-100'
                    : ins.type === 'warning' ? 'text-amber-800 bg-amber-50 border-amber-100'
                    : 'text-stone-700 bg-stone-50 border-stone-100';
                  return (
                    <li key={i} data-testid={`insight-item-${i}`} className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm ${color}`}>
                      <Icon size={18} weight="duotone" className="mt-0.5 flex-shrink-0"/>
                      <span>{ins.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div data-testid="top-selling" className="bg-white border border-stone-200 rounded-2xl p-5">
              <h3 className="font-heading text-base text-stone-900 mb-3">Top Selling (30d)</h3>
              {insights.top_selling.length === 0 ? <p className="text-sm text-stone-500">No sales yet.</p> : (
                <ul className="space-y-2 text-sm">
                  {insights.top_selling.map((p) => (
                    <li key={p.product_id} className="flex justify-between gap-3">
                      <span className="text-stone-800 truncate">{p.name}</span>
                      <span className="text-stone-500 whitespace-nowrap">{p.qty} · {formatINR(p.revenue)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {insights.best_category && (
                <p className="mt-3 text-xs text-olive-700 bg-olive-50 rounded-lg px-2 py-1">
                  Best category: <span className="font-semibold">{insights.best_category.category_slug.replace(/-/g, ' ')}</span> · {formatINR(insights.best_category.revenue)}
                </p>
              )}
            </div>

            <div data-testid="stock-alerts" className="bg-white border border-stone-200 rounded-2xl p-5">
              <h3 className="font-heading text-base text-stone-900 mb-3">Stock Alerts</h3>
              {insights.out_of_stock.length === 0 && insights.low_stock.length === 0 ? (
                <p className="text-sm text-stone-500">All products well-stocked.</p>
              ) : (
                <div className="space-y-3 text-sm">
                  {insights.out_of_stock.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-1">Out of Stock ({insights.out_of_stock.length})</p>
                      <ul className="space-y-1">
                        {insights.out_of_stock.slice(0, 5).map((p) => (
                          <li key={p.id} className="flex justify-between gap-3 text-stone-800">
                            <span className="truncate">{p.name}</span>
                            <span className="text-red-700 whitespace-nowrap">{p.stock}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {insights.low_stock.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1">Low Stock ({insights.low_stock.length})</p>
                      <ul className="space-y-1">
                        {insights.low_stock.slice(0, 5).map((p) => (
                          <li key={p.id} className="flex justify-between gap-3 text-stone-800">
                            <span className="truncate">{p.name}</span>
                            <span className="text-amber-800 whitespace-nowrap">{p.stock} left</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div data-testid="slow-moving" className="bg-white border border-stone-200 rounded-2xl p-5">
              <h3 className="font-heading text-base text-stone-900 mb-3">Slow Moving (30d)</h3>
              {insights.slow_moving.length === 0 ? (
                <p className="text-sm text-stone-500">All in-stock products have recent sales.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {insights.slow_moving.slice(0, 6).map((p) => (
                    <li key={p.id} className="text-stone-800 truncate">{p.name}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div
            key={c.label}
            data-testid={c.testId}
            className="bg-white border border-stone-200 rounded-2xl p-4"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs text-stone-500 uppercase tracking-wider">{c.label}</p>
              <c.icon size={20} weight="duotone" className={c.color === 'olive' ? 'text-olive-700' : 'text-terracotta-700'} />
            </div>
            <p className="font-heading text-2xl text-stone-900 mt-2">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-5">
        <h2 className="font-heading text-lg text-stone-900 mb-3">Recent Orders</h2>
        {orders.length === 0 ? (
          <p className="text-sm text-stone-500">No orders yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-stone-500 text-xs uppercase tracking-wider">
                  <th className="py-2 pr-3">Order</th>
                  <th className="pr-3">Customer</th>
                  <th className="pr-3">Mobile</th>
                  <th className="pr-3">Status</th>
                  <th className="pr-3">Total</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const cls = STATUS_PALETTE[o.status] || 'bg-cream-200 text-stone-800';
                  const label = STATUS_LABEL[o.status] || o.status;
                  return (
                    <tr key={o.id} className="border-t border-stone-100">
                      <td className="py-2.5 pr-3 font-medium text-stone-900">{o.order_no}</td>
                      <td className="pr-3">{o.customer_name || o.delivery_address?.name || '—'}</td>
                      <td className="pr-3 text-stone-600">{o.customer_mobile || o.delivery_address?.phone || '—'}</td>
                      <td className="pr-3"><span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{label}</span></td>
                      <td className="pr-3 font-medium">{formatINR(o.total)}</td>
                      <td className="text-stone-500 text-xs">{new Date(o.created_at).toLocaleDateString('en-IN')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
